import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  input,
  model,
  OnChanges,
  OnInit,
  Output,
  output,
  signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { debounceTime, map, Observable, startWith } from 'rxjs';
import { ComboBoxInputData } from '../combo-input/combo-input.component';
import { MultiComboEntitySearchBase } from './multi-combo-entity-search.base';

interface HasId {
  id: string
}

@Component({
  selector: 'pixl-multi-combo-entity-search',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatIconModule,
    MatSelect
  ],
  templateUrl: './multi-combo-entity-search.component.html',
  styleUrl: './multi-combo-entity-search.component.scss'
})
export class MultiComboEntitySearchComponent<T extends HasId> extends MultiComboEntitySearchBase implements OnInit, OnChanges {
  private static openedInstance: MultiComboEntitySearchBase | null = null;
  @Input() observable$!: Observable<T[]>;
  @Input() namingColumn!: keyof T; // the name of the column wich should be displayed
  withEmptyOption = input<boolean>(false); // Should '---' displayed in options list?
  externalSearch = input<boolean>(false);
  multiple = input<boolean>(true);
  label = input<string>();
  placeHolder = input<string>('');
  onStateChange = output<T[] | null>();
  onSearchChange = output<string>();
  dataList = input<ComboBoxInputData[]>([]);
  selectedValues = model<T[]>([]); // this is a two-way-binding to a part of a from group in the parent component so this component knows which entrys are selected

  required = input<boolean>(false);
  isEmpty = signal(false);
  isLoading = signal(false);

  inputControl = new FormControl('');
  searchValue: string | null = null;
  selectControl = new FormControl<T[]>([]);
  filteredOptions!: Observable<ComboBoxInputData[]>;
  allVisibleOptions: T[] = [];

  @ViewChild('myInput') myInput!: ElementRef<HTMLInputElement>;
  @ViewChild('mySelect') mySelect!: MatSelect;

  currentPage = 1;
  pageSize = 20;
  currentOpenedSelect: MatSelect | null = null;
  @Output() scrolledToEnd = new EventEmitter<{ pageNumber: number, pageSize: number }>();

  private lastClosedTime = 0;

  close(): void {
    this.mySelect.close();
  }

  ngOnInit() {
    if (!this.externalSearch()) {
      this.filteredOptions = this.inputControl.valueChanges.pipe(
        startWith(this.inputControl.value as string),
        debounceTime(200),
        map(value => {
          return this._filter(value || '');
        })
      );
    }

    if (this.observable$) {
      this.handleObservable(this.observable$);
    }
  }

  onSelectOpened(opened: boolean, openedSelect: MatSelect) {
    if (opened) {
      this.currentOpenedSelect = openedSelect;

      setTimeout(() => {
        const panel = openedSelect.panel?.nativeElement;
        if (panel) {
          panel.addEventListener('scroll', this.onScroll.bind(this));
        }
      }, 0);
    }
    else {
      this.currentOpenedSelect = null;
      // Record when the panel was closed to prevent onClick from immediately reopening it
      this.lastClosedTime = Date.now();
    }
  }

  onScroll(event: Event) {
    if (!this.currentOpenedSelect) return;

    const target = event.target as HTMLElement;

    // detect scroll end
    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      this.currentPage += 1;

      // emit event to parent for loading next page
      this.scrolledToEnd.emit({ pageNumber: this.currentPage, pageSize: this.pageSize });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.observable$?.currentValue) {
      this.handleObservable(changes.observable$.currentValue);
    }

    if (changes.selectedValues) {
      if (this.multiple()) {
        this.selectControl.setValue(this.selectedValues(), { emitEvent: false });
      }
      else {
        const selected = this.selectedValues()?.[0] ?? null;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.selectControl.setValue(selected as any, { emitEvent: false });
        this.inputControl.setValue(selected ? this.getDisplayValue(selected) : '', { emitEvent: false });
      }
    }
  }

  private handleObservable(obs: Observable<T[]>) {
    obs.subscribe({
      next: (newResults) => {
        this.isLoading.set(false);

        const selected = this.selectedValues() ?? [];
        const combined = [
          ...selected,
          ...newResults.filter(x => !selected.some(sel => this.compare(sel, x))),
        ];

        this.allVisibleOptions = combined;
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  private compare(a: T, b: T): boolean {
    return a.id === b.id;
  }

  getDisplayValue(entry: T): string {
    const value1 = entry[this.namingColumn];

    if (value1) {
      return String(value1);
    }
    else {
      return '';
    }
  }

  private _filter(value: string): ComboBoxInputData[] {
    const filterValue = value.toLowerCase();
    const currentDataList = this.dataList();

    // Check if entry is empty
    if (filterValue === '') {
      return currentDataList;
    }

    // Check if the input value exactly matches a 'text' field in the dataList.
    // This is the case if an item has been selected from the list.
    const isExactTextMatch = currentDataList.some(item => {
      const itemTextLower = item.text.toLowerCase();
      return itemTextLower === filterValue;
    });

    if (isExactTextMatch) {
      return currentDataList;
    }

    // Otherwise (if the user is typing and it's not an exact match),
    // we filter the 'ComboBoxInputData' objects based on the input in the 'text' field.
    const filteredResults = currentDataList.filter(item => {
      const itemTextLower = item.text.toLowerCase();
      const includesMatch = itemTextLower.includes(filterValue);
      return includesMatch;
    });
    return filteredResults;
  }

  onInputChanged(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;

    this.searchValue = value;
    this.inputControl.setValue(value);

    if (!this.mySelect.panelOpen) {
      this.openSelect();
    }

    if (this.externalSearch()) {
      this.currentPage = 1;
      this.isLoading.set(true);
      this.onSearchChange.emit(value! || '');
    }
  }

  onOptionSelected(event: MatSelectChange): void {
    this.isEmpty.set(false);

    if (this.required() && !this.selectControl && !event) {
      this.isEmpty.set(true);
      return;
    }

    if (!this.multiple()) {
      const selected = event.value as T;
      const wrapped = selected ? [selected] : [];
      this.selectedValues.set(wrapped);
      this.inputControl.setValue(selected ? this.getDisplayValue(selected) : '');
      this.mySelect.close();
      this.onStateChange.emit(wrapped);
    }
    else {
      this.onStateChange.emit(this.selectControl.value);
    }
  }

  public openSelect() {
    if (MultiComboEntitySearchComponent.openedInstance &&
      MultiComboEntitySearchComponent.openedInstance !== this) {
      MultiComboEntitySearchComponent.openedInstance.close();
    }
    MultiComboEntitySearchComponent.openedInstance = this;
    if (!this.mySelect.panelOpen) {
      this.mySelect.open();
    }
  }

  public onClick(event?: Event) {
    // Prevent reopening if the panel was just closed (e.g. by backdrop click)
    if (Date.now() - this.lastClosedTime < 300) {
      return;
    }
    if (this.mySelect.panelOpen) {
      if (event && (event.target === this.myInput.nativeElement || this.mySelect.panel?.nativeElement?.contains(event.target))) {
        return;
      }
      this.mySelect.close();
    }
    else {
      this.openSelect();
    }
  }
}
